import { Box } from '@material-ui/core';
import { useBaseStore, useMiscellaneousRoutes } from '@roc/feature-app-core';
import {
  ChatRoomType,
  FilterType,
  useCommunicationRoutesConfig,
  useLoanCommunicationStore,
} from '@roc/feature-communication';
import { useDocumentStore } from '@roc/feature-documents';
import { CapitalProvider, LoanProcess } from '@roc/feature-utils';
import { BreadcrumbsNew } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useBorrowerLoansStore } from '../../hooks/useBorrowerLoansStore';
import { useBorrowerLoansRoutes } from '../../hooks/useBorrowerLoansRoutes';
import { BorrowerLoanDetailsFAB } from './components/borrowerLoanDetailsFAB';
import { BorrowerLoanDetailsChildRoutesV3 } from './components/borrowerLoanDetailsChildRoutesV3';

export const BorrowerInternalCapitalLoanDetailsV3 = observer(() => {
  return (
    <BorrowerLoanDetailsV3
      isPlatformLoan={false}
      source={CapitalProvider.PLATFORM_CAPITAL}
    />
  );
});

export const BorrowerExternalCapitalLoanDetailsV3 = observer(() => {
  return (
    <BorrowerLoanDetailsV3
      isPlatformLoan={true}
      source={CapitalProvider.EXTERNAL_CAPITAL}
    />
  );
});

export const BorrowerLoanDetailsV3 = observer(
  ({
    isPlatformLoan,
    source,
  }: {
    isPlatformLoan?: boolean;
    source: CapitalProvider;
  }) => {
    const params = useParams<{ loanId: string; loanNumber: string }>();
    const loanId = params.loanId || params.loanNumber;

    const [
      doesUserHasAnyConversations,
      setDoesUserHasAnyConversations,
    ] = useState<boolean>(false);
    const { borrowerLoansStore } = useBorrowerLoansStore();
    const {
      borrowerLoanRoutesConfig,
      platformBorrowerLoanRoutesConfig,
    } = useBorrowerLoansRoutes();
    const { communicationRoutesConfig } = useCommunicationRoutesConfig();
    const { documentStore } = useDocumentStore();
    const { loanCommunicationStore } = useLoanCommunicationStore();
    const { miscellaneousRoutesConfig } = useMiscellaneousRoutes();
    const { push } = useHistory();
    const { pathname } = useLocation();

    const activeLoanRoutesConfig = isPlatformLoan
      ? platformBorrowerLoanRoutesConfig
      : borrowerLoanRoutesConfig;

    const { loanDetails } = borrowerLoansStore;
    const { globalStore } = useBaseStore();
    const {
      breadCrumbsStore: { breadCrumbItems },
    } = globalStore;
    const [conversationSidToUse, setConversationSidToUse] = useState<string>();

    useEffect(() => {
      if (borrowerLoansStore.isLoanDetailsSuccess === false) {
        push(miscellaneousRoutesConfig.notFound.url);
        borrowerLoansStore.setLoanDetailsSuccess(null);
      }
    }, [borrowerLoansStore.isLoanDetailsSuccess]);

    useEffect(() => {
      const dashboardChildRoutes = activeLoanRoutesConfig.loans(loanId)
        .children;
      const tabRouteUrls: Record<string, string> = {
        'loan-summary': dashboardChildRoutes.dashboard.url,
        tasks: isPlatformLoan
          ? platformBorrowerLoanRoutesConfig.loans(loanId).children.tasks.url
          : borrowerLoanRoutesConfig.loans(loanId).children.borrowerTodos.url,
        draws: dashboardChildRoutes.draws.url,
        extensions: dashboardChildRoutes.extensions.url,
        payoffs: dashboardChildRoutes.payoffRequest.url,
      };

      const handleTabChange = (e: Event) => {
        const { detail } = e as CustomEvent<{ loanId: number; tab: string }>;
        if (String(detail?.loanId) !== String(loanId)) return;
        const url = tabRouteUrls[detail?.tab];
        if (url && pathname.indexOf(url) === -1) {
          push(url);
        }
      };

      window.addEventListener(
        'platform-ui:borrower-loan:tab-change',
        handleTabChange
      );
      return () => {
        window.removeEventListener(
          'platform-ui:borrower-loan:tab-change',
          handleTabChange
        );
      };
    }, [loanId, pathname, push, activeLoanRoutesConfig]);

    useEffect(() => {
      const dashboardChildRoutes = activeLoanRoutesConfig.loans(loanId)
        .children;
      const tabRouteUrls: Record<string, string> = {
        'loan-summary': dashboardChildRoutes.dashboard.url,
        tasks: isPlatformLoan
          ? platformBorrowerLoanRoutesConfig.loans(loanId).children.tasks.url
          : borrowerLoanRoutesConfig.loans(loanId).children.borrowerTodos.url,
        draws: dashboardChildRoutes.draws.url,
        extensions: dashboardChildRoutes.extensions.url,
        payoffs: dashboardChildRoutes.payoffRequest.url,
      };

      let matchedTab: string | undefined;
      Object.entries(tabRouteUrls).forEach(([tab, url]) => {
        if (pathname.indexOf(url) !== -1) {
          matchedTab = tab;
        }
      });

      if (matchedTab) {
        window.dispatchEvent(
          new CustomEvent('platform-ui:borrower-loan:sync-tab', {
            detail: { loanId: Number(loanId), tab: matchedTab, source },
          })
        );
      }
    }, [loanId, pathname, source, activeLoanRoutesConfig]);

    useEffect(() => {
      borrowerLoansStore.fetchLoanDetails(loanId, source);
      if (!isPlatformLoan) {
        documentStore.loanTodosStore.fetchTodos(
          Number(loanId),
          LoanProcess.UNDERWRITING
        );
      }
    }, [loanId, source]);

    useEffect(() => {
      if (loanDetails?.loanId) {
        loanCommunicationStore
          .fetchUserConversations([
            {
              type: FilterType.LOAN_ID,
              value: loanDetails.loanId,
            },
          ])
          .then(x => {
            if (x?.totalCount) {
              if (loanDetails?.showDraws) {
                // After funding we show all rooms to the borrower
                setDoesUserHasAnyConversations(true);
              } else {
                // Till Pre-Funding stage, we show the NEW_LOAN_REQUEST room only
                const y = x.conversationsData?.filter(cData => {
                  return cData.chatRoomType == ChatRoomType.NEW_LOAN_REQUEST;
                })[0];
                if (y?.conversationSid) {
                  setConversationSidToUse(y.conversationSid);
                  setDoesUserHasAnyConversations(true);
                }
              }
            }
          });
        loanCommunicationStore.fetchUnreadMessagesCount(loanId);
      }
    }, [loanDetails?.loanId]);

    const handleOpenConversationsDialog = () => {
      if (conversationSidToUse) {
        push(
          communicationRoutesConfig
            .conversations(loanId)
            .children.conversation(conversationSidToUse).url,
          { from: pathname }
        );
      } else {
        push(communicationRoutesConfig.conversations(loanId).url, {
          from: pathname,
        });
      }
    };

    return (
      <Box my={3}>
        <Box display="flex" flexDirection="column" mb={3}>
          <Box mb={1.5}>
            <BreadcrumbsNew
              breadCrumbItems={breadCrumbItems}
              separator={<span>&#187;</span>}
            />
          </Box>
        </Box>
        <Box display="flex">
          <Box flexGrow={1}>
            <BorrowerLoanDetailsChildRoutesV3
              isPlatformLoan={!!isPlatformLoan}
            />
          </Box>
        </Box>

        <BorrowerLoanDetailsFAB
          showConversations={doesUserHasAnyConversations}
          loanCommunicationStore={loanCommunicationStore}
          onOpenConversations={handleOpenConversationsDialog}
        />
      </Box>
    );
  }
);
